package com.dynamicruntime.common.gedra

import com.dynamicruntime.common.endpoint.EI
import com.dynamicruntime.common.endpoint.EP
import com.dynamicruntime.common.gedra.workflow.SVY
import com.dynamicruntime.common.gedra.workflow.WAGG
import com.dynamicruntime.common.schema.SCH
import com.dynamicruntime.common.schema.SCT
import com.dynamicruntime.common.schema.SFMT
import com.dynamicruntime.common.schema.SchOption
import com.dynamicruntime.common.schema.parseExactBool
import com.dynamicruntime.common.schema.qualifyTypeName
import com.dynamicruntime.common.util.parseDateOrNull
import com.dynamicruntime.common.util.serverTimeZone
import com.dynamicruntime.common.util.toJsonMapOrEmpty
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.plus

/**
 * Turns a client's trait-usage rules into forms-list **search** (issue #538): the parameters the listing
 * endpoint advertises, and the predicate that filters a page by them. Pure and in `base/kernel`, beside the
 * usage model it reads -- the same computed **display value** a listing shows in a column is the value a
 * search matches against, so what a person sees is what they search, in one place.
 *
 * A column contributes a parameter by its search shape's kind ([UsageColumn.shape], issue #1508): a `string` an
 * exact one (and a substring one when the usage asks -- [ClientTraitUsage.substring]); a `number` or `date` a
 * `>=`/`<=` pair, so a value can be bounded from either end or both. The parameter names are derived here and read nowhere by hand -- the
 * schema properties and the predicate are both built from [gedraSearchParams], so an advertised parameter and
 * the field the predicate reads cannot drift.
 */
@Suppress("EnumEntryName")
enum class SearchRole(
    /**
     * What the role appends to the column's name ([ClientTraitUsage.columnKey]) to make the parameter's wire name:
     * `name` for the exact match, `nameContains`, `yearMin`, `yearMax`. [gedraSearchParams] writes it and
     * [decodeSearchParam] reads it back, so a control that groups a column's parameters (issue #562) cannot disagree
     * with the names it must send.
     */
    val nameSuffix: String,
    /** What the role appends to the column label for a control's caption, said in words. */
    val labelSuffix: String,
) {
    /** A `string` exact match (case-insensitive), or a `boolean`'s yes-or-no (issue #1508). */
    exact("", ""),

    /** A `string` substring match (case-insensitive). */
    contains("Contains", " (contains)"),

    /** A `number`/`date` lower bound: the value is `>=` this. */
    min("Min", " (min)"),

    /** A `number`/`date` upper bound: the value is `<=` this. */
    max("Max", " (max)"),
}

/**
 * One search parameter a usage contributes: its wire [name], the column [label] it belongs to, the [columnKey]
 * whose display value it filters ([ClientTraitUsage.columnKey]), the [role] (how to compare), the [kind] (how to
 * read both sides), and the column's [choices] (issue #1500), whose labels a text match also reads and the schema
 * property offers (issue #1508). Built once by [gedraSearchParams] and used to
 * generate the schema property *and* to run the predicate, so the two stay in step.
 */
class GedraSearchParam(
    val name: String,
    val label: String,
    val columnKey: String,
    val role: SearchRole,
    val kind: UsageKind,
    val choices: List<SchOption> = emptyList(),
)

/**
 * The search parameters [columns] contribute, in declaration order. A `string` gives an exact parameter named for the
 * column ([ClientTraitUsage.columnKey]: the trait id, unless the column has a name of its own -- issue #1490) and,
 * when [ClientTraitUsage.substring] is set, a `<column>Contains` beside it; a `number` or `date` gives `<column>Min`
 * and `<column>Max`. Which it is, and the choices the parameters carry, are the column's search shape
 * ([UsageColumn.shape], issue #1508).
 *
 * A usage one of whose names the listing keeps for itself ([reservedQueryFieldNames]) contributes **nothing**
 * (issue #987): it is left out here, at the one place every reader starts from, so the advertised schema and
 * the predicate drop it together -- a predicate that still read it would filter by the listing's own `offset`.
 * The whole usage goes, not the one name: a `<column>Contains` left standing alone would read back
 * ([decodeSearchParam]) as the exact match of some other column. The usage keeps its column;
 * [searchParamCollisions] is what reports it.
 */
fun gedraSearchParams(columns: List<UsageColumn>): List<GedraSearchParam> = columns.flatMap { column ->
    val minted = mintedSearchParams(column)
    if (minted.any { it.name in reservedQueryFieldNames }) emptyList() else minted
}

/** Every parameter [column] would mint by name alone, a reserved name included -- see [gedraSearchParams]. */
private fun mintedSearchParams(column: UsageColumn): List<GedraSearchParam> = buildList {
    val shape = column.shape
    fun param(role: SearchRole) = GedraSearchParam(
        column.columnKey + role.nameSuffix, column.label, column.columnKey, role, shape.kind, shape.choices,
    )
    when (shape.kind) {
        UsageKind.string -> {
            add(param(SearchRole.exact))
            if (column.usage.substring) add(param(SearchRole.contains))
        }
        UsageKind.number, UsageKind.date -> {
            add(param(SearchRole.min))
            add(param(SearchRole.max))
        }
        UsageKind.boolean -> add(param(SearchRole.exact))
    }
}

/**
 * The JSON-Schema `properties` fragment for [params] -- a `{ name -> propertySchema }` map to merge into the
 * listing's query type. Every parameter is optional (a search is), and a query param arrives as text, so a
 * `number` bound carries `g-allowCoerce` (a `date` coerces from its format already; see the schema layer).
 *
 * Generated from the parameter's few facts, never copied from the field's schema (issue #1508): a text parameter of a
 * column with choices offers them -- **open**, since a search for a value that is not there is a harmless answer, and
 * a value from before the client narrowed its choices must stay findable. A number bound is a `number`, an integer
 * field's too, for the same reason.
 */
fun searchParamProperties(params: List<GedraSearchParam>): Map<String, Any?> {
    val props = LinkedHashMap<String, Any?>()
    for (param in params) {
        val prop = LinkedHashMap<String, Any?>()
        prop[SCH.title] = searchLabel(param)
        when {
            // A yes-or-no (issue #1508): typed, so it coerces from `yes`, `1`, `on` and their opposites.
            param.kind == UsageKind.boolean -> {
                prop[SCH.type] = SCT.boolean
                prop[SCH.description] = "Whether the '${param.columnKey}' value is true or false."
            }
            // "Or its label" (issue #1500): a column whose field offers choices shows, and matches, the choice's label.
            param.role == SearchRole.contains ->
                prop[SCH.description] = "Substring match (case-insensitive) on the '${param.columnKey}' value, or its label."
            param.role == SearchRole.exact ->
                prop[SCH.description] = "Exact match (case-insensitive) on the '${param.columnKey}' value, or its label."
            param.kind == UsageKind.number -> {
                prop[SCH.type] = SCT.number
                prop[SCH.allowCoerce] = true
                prop[SCH.description] = boundDescription(param)
            }
            else -> { // a date bound
                prop[SCH.type] = SCT.string
                prop[SCH.format] = SFMT.date
                prop[SCH.description] = boundDescription(param)
            }
        }
        if (param.choices.isNotEmpty() && (param.role == SearchRole.exact || param.role == SearchRole.contains)) {
            prop[SCH.options] = param.choices.map { mapOf(SCH.label to it.label, SCH.value to it.value) }
            prop[SCH.openOptions] = true
        }
        props[param.name] = prop
    }
    return props
}

/** The short label a search control shows for [param] -- the column label, with the variant said in words. */
private fun searchLabel(param: GedraSearchParam): String = param.label + param.role.labelSuffix

/**
 * One search parameter read back off the listing's input schema (issue #562): the column ([columnKey]) whose display
 * value it filters, its [role], and the [kind] both sides are read as -- what [gedraSearchParams] wrote, recovered
 * from the wire name and the property's schema. For a UI that groups a column's parameters into one control (a
 * text box, or a from-to pair) rather than showing each as a peer.
 */
class SearchParamShape(val columnKey: String, val role: SearchRole, val kind: UsageKind)

/**
 * The inverse of [gedraSearchParams]'s naming, for one property [name] with its schema [prop] among the
 * [declared] property names of the same query type: the column, the role, and the kind the parameter was
 * generated from.
 *
 * The schema decides the family and the spelling decides the role within it: a `number` type or `date` format
 * says the parameter is a bound, and then a `Min`/`Max` ending says which. Plain text ending in `Contains` is
 * the substring parameter **only when the name it shortens to is declared too** -- the generator never writes a
 * substring parameter without the exact one beside it -- so a column whose own name ends in `Contains` reads as
 * the exact match on its full name rather than as some other column's substring. Reading the kind first does the
 * same for a text column whose name ends in `Min`. A parameter the generator would not have written -- a typed one
 * with neither ending -- reads as an exact match on its own name, so it still gets a control rather than
 * vanishing.
 */
fun decodeSearchParam(name: String, prop: Map<String, Any?>, declared: Set<String>): SearchParamShape {
    val kind = when {
        prop[SCH.type] == SCT.boolean -> UsageKind.boolean
        prop[SCH.type] == SCT.number -> UsageKind.number
        prop[SCH.format] == SFMT.date -> UsageKind.date
        else -> UsageKind.string
    }
    fun hasSuffix(role: SearchRole) = name.length > role.nameSuffix.length && name.endsWith(role.nameSuffix)
    val role = when (kind) {
        UsageKind.string ->
            if (hasSuffix(SearchRole.contains) && name.removeSuffix(SearchRole.contains.nameSuffix) in declared) SearchRole.contains
            else SearchRole.exact
        UsageKind.number, UsageKind.date -> when {
            hasSuffix(SearchRole.min) -> SearchRole.min
            hasSuffix(SearchRole.max) -> SearchRole.max
            else -> SearchRole.exact
        }
        UsageKind.boolean -> SearchRole.exact
    }
    return SearchParamShape(name.removeSuffix(role.nameSuffix), role, kind)
}

private fun boundDescription(param: GedraSearchParam): String {
    val relation = if (param.role == SearchRole.min) "at or above" else "at or below"
    val wholeDay = if (param.role == SearchRole.max && param.kind == UsageKind.date) " A day alone includes the whole day." else ""
    return "Keep rows whose '${param.columnKey}' value is $relation this.$wholeDay"
}

/** The `$defs` key of the listing's query type: [GEP.formDocsQuery] qualified in the gedra namespace. */
fun formDocsQueryDefName(): String = qualifyTypeName(GEP.formDocsQuery, GEP.gedraNamespace)

/**
 * The listing query's **stable** field names -- every field the query type declares for itself, and the two the
 * framework appends when the endpoint's input resolves: the page `limit`, and the cursor `after` (issue #987) of
 * a cursor-paged listing. A generated search parameter must not take one. `after` is held ahead of need: the
 * forms listing pages by offset today, and reserving the name now means a later move to cursor paging cannot
 * turn a client's trait name into a refused boot.
 *
 * The one list (issue #987): [gedraSearchParams] leaves these names out, [searchParamCollisions] reports the
 * usage that asked for one, and the webapp reads the same set to tell a trait search box from the listing's own
 * controls. A field added to the query type belongs here too, which `FormDocsQueryReservedTest` holds.
 */
val reservedQueryFieldNames: Set<String> = setOf(
    EP.offset, EP.limit, EP.after, EI.user, EI.client, EI.q, EI.includeUsers, GSORT.sort, GSORT.sortDir,
    GDF.withStates, GDF.withWorkflowSummary, SVY.surveyStatus, WAGG.workflowId, WAGG.workflowState,
)

/**
 * The search parameter names [columns] ask for that are a [reservedQueryFieldNames] entry -- the boot check. A
 * `string` usage on a trait named `user` (say) asks for an exact parameter `user`, the listing's own user
 * filter; [gedraSearchParams] leaves the usage out, so the trait shows as a column and cannot be searched. Empty is the
 * ordinary case; a non-empty result is a client-config mistake to report.
 */
fun searchParamCollisions(columns: List<UsageColumn>): List<String> =
    columns.flatMap { mintedSearchParams(it) }.map { it.name }.filter { it in reservedQueryFieldNames }.distinct()

/**
 * The names two of [columns] would share (issues #681, #1490), in first-seen order and without repeats -- the boot
 * check, which a write's trial also runs. The presentation model is keyed by the column's name
 * ([ClientTraitUsage.columnKey]): a row's display values are a map keyed by it (a second column's value would
 * silently overwrite the first's), `matchesSearch` reads a parameter's value by it, the sort resolves its column by
 * it, and the frontend keys a column by it -- so two columns of one name are one broken column. Their search
 * parameters must not meet either: a text column named `yearMin` would take a number column `year`'s lower bound.
 * Two columns of one **trait** are fine, as long as their names differ. Empty is the ordinary case; a non-empty
 * result is a client-config mistake to report.
 */
fun usageColumnClashes(columns: List<UsageColumn>): List<String> {
    val keys = columns.groupBy { it.columnKey }.filter { it.value.size > 1 }.keys
    val params = columns.flatMap { column -> mintedSearchParams(column).map { it.name to column.columnKey }.distinct() }
        .groupBy({ it.first }, { it.second }).filter { it.value.distinct().size > 1 }.keys
    return (keys + params).distinct()
}

/**
 * [baseDef] (a JSON-Schema object type -- the listing's stable query type) with the search properties [columns]
 * contribute merged into its `properties` (issue #538). The base is returned untouched when the columns
 * contribute nothing, so a scope with no usages shares the base rather than a distinct-but-equal copy -- which
 * is what lets the per-client build hand back the global type when a client's search fields do not differ.
 *
 * A search property never **overwrites** a field the base already declares: the base keeps it. That cannot
 * arise while every base field is in [reservedQueryFieldNames], which [gedraSearchParams] leaves out; this is
 * the structural backstop for a field added to the type and not to the list.
 */
fun withSearchProperties(baseDef: Any?, columns: List<UsageColumn>): Map<String, Any?> {
    val base = baseDef.toJsonMapOrEmpty()
    val props = searchParamProperties(gedraSearchParams(columns))
    if (props.isEmpty()) return base
    val merged = LinkedHashMap(base[SCH.properties].toJsonMapOrEmpty())
    for ((name, prop) in props) {
        if (!merged.containsKey(name)) merged[name] = prop
    }
    return base + (SCH.properties to merged)
}

/**
 * Whether a row satisfies every active search parameter (AND across them), given the row's display values keyed
 * by column ([displayByColumn], [ClientTraitUsage.columnKey]) and the [active] parameters paired with the value the
 * caller supplied. A row that carries no value for a parameter's column -- an empty display string -- matches none
 * of that column's parameters, which drops it from a search rather than faulting the page. A date bound given as a day is the
 * start of that day in [zone], the client's (issue #1115).
 */
fun matchesSearch(
    displayByColumn: Map<String, String>,
    active: List<Pair<GedraSearchParam, String>>,
    zone: TimeZone = serverTimeZone,
): Boolean = active.all { (param, query) -> matchesOne(displayByColumn[param.columnKey].orEmpty(), param, query, zone) }

/** The text columns -- the fields the free-text term ([EI.q]) searches across. */
fun textSearchColumns(columns: List<UsageColumn>): List<UsageColumn> = columns.filter { it.kind == UsageKind.string }

/**
 * Whether a row's display values match the free-text term [query] (issue #562): a case-insensitive substring
 * match against **any** of the [textColumns] (OR across fields), so one box finds a document by whichever of
 * its searchable text values the person remembers -- a value's label too, for a column with choices (issue #1500).
 * A blank term matches everything -- an empty box is no search, not a search for nothing -- and a row with no text
 * values matches only a blank term.
 */
fun matchesAnyText(displayByColumn: Map<String, String>, textColumns: List<UsageColumn>, query: String): Boolean {
    val term = query.trim()
    if (term.isEmpty()) return true
    return textColumns.any { column ->
        valueAndLabel(displayByColumn[column.columnKey].orEmpty(), column.choices).any { it.contains(term, ignoreCase = true) }
    }
}

/**
 * [value], and its label when [choices] give it one (issue #1500): what a text search on the column matches, since
 * the column shows the label, and a person types or pastes what they see.
 */
private fun valueAndLabel(value: String, choices: List<SchOption>): List<String> =
    listOfNotNull(value, choiceLabel(value, choices))

private fun matchesOne(value: String, param: GedraSearchParam, query: String, zone: TimeZone): Boolean = when {
    // Read as booleans (issue #1508), so `yes` finds a true value; a blank value is no answer, and matches neither.
    param.kind == UsageKind.boolean -> parseExactBool(value)?.let { it == parseExactBool(query) } ?: false
    else -> matchesText(value, param, query, zone)
}

private fun matchesText(value: String, param: GedraSearchParam, query: String, zone: TimeZone): Boolean = when (param.role) {
    SearchRole.exact -> valueAndLabel(value, param.choices).any { it.trim().equals(query.trim(), ignoreCase = true) }
    SearchRole.contains -> valueAndLabel(value, param.choices).any { it.contains(query.trim(), ignoreCase = true) }
    // A bound over a value that is not a number/date at all -- an empty or malformed display -- is a non-match,
    // not a fault: a range asks "is this within the bounds", and a value that is not on the line is not.
    SearchRole.min -> compareBound(value, param.kind, query, zone)?.let { it >= 0 } ?: false
    SearchRole.max ->
        if (param.kind == UsageKind.date && isDayText(query.trim())) {
            beforeDayEnds(value, query.trim(), zone)
        } else {
            compareBound(value, param.kind, query, zone)?.let { it <= 0 } ?: false
        }
}

/**
 * Whether the date [value] falls before the end of [day] in [zone] (issue #1500 review): a day as a date range's upper
 * bound includes the whole of that day, as a day picker reads, rather than stopping at its first moment.
 */
private fun beforeDayEnds(value: String, day: String, zone: TimeZone): Boolean {
    val v = value.parseDateOrNull(zone) ?: return false
    val end = LocalDate.parse(day).plus(1, DateTimeUnit.DAY).atStartOfDayIn(zone)
    return v < end
}

/** Compares a row's [value] to the [query] bound as [kind]; null when either side does not read as that kind. */
private fun compareBound(value: String, kind: UsageKind, query: String, zone: TimeZone): Int? = when (kind) {
    UsageKind.number -> {
        val v = value.trim().toDoubleOrNull()
        val q = query.trim().toDoubleOrNull()
        if (v == null || q == null) null else v.compareTo(q)
    }
    UsageKind.date -> {
        val v = value.parseDateOrNull(zone)
        val q = query.parseDateOrNull(zone)
        if (v == null || q == null) null else v.compareTo(q)
    }
    // A string or a boolean is never a bound; guarded by construction (it makes no min/max param), null here.
    UsageKind.string, UsageKind.boolean -> null
}

/**
 * The **sort** parameter names on the listing query (issue #666): which column to order by, and the direction.
 * Reserved like the other stable field names so a client trait cannot mint a search parameter that collides
 * with them (see [reservedQueryFieldNames], into which these are folded).
 */
@Suppress("ConstPropertyName")
object GSORT {
    /** The column to sort by: a display trait id, or [updated] / [created]. Absent means the default order. */
    const val sort = "sort"

    /** The direction: [asc] or [desc]. Absent (with a column named) means ascending. */
    const val sortDir = "sortDir"

    /** Sort by the row's last-updated date -- the listing's default column. */
    const val updated = "updated"

    /** Sort by the row's created date. */
    const val created = "created"

    /** Sort by the "Contains" summary -- the row's traits (issue #666), ordered by their ids as text. */
    const val contains = "contains"

    /** Sort by the owner (the User column, issue #666), by the name the column shows. Admin-only, as the column
     *  is: an ordinary caller sees only their own rows, so there is no one to order by. */
    const val owner = "owner"

    /** Sort by the row's client (the Client column, issue #668). `allClients`-only, as the column is: a caller
     *  who does not see across clients has only their own client's rows, so there is nothing to order by. */
    const val client = "client"

    const val asc = "asc"
    const val desc = "desc"

    /**
     * The wire prefix a **display-column** sort key carries (issue #666), so a column named like a fixed one
     * (`updated`/`created`) cannot collide with it: a display column sorts under `display_<column>` (its
     * [ClientTraitUsage.columnKey], issue #1490), a fixed column under its bare name. The same `display_` the forms
     * table keys its display *cells* by, kept here so the endpoint's decode and the table's encode read one constant.
     */
    const val displayColumnPrefix = "display_"

    /** The display column ([ClientTraitUsage.columnKey]) a sort [column] names ([displayColumnPrefix] stripped), or
     *  null when it is a fixed column (`updated`/`created`) or otherwise not a display key -- so a column called
     *  `updated` still decodes to its own value rather than the protocol date. */
    fun displayColumnKey(column: String): String? =
        if (column.startsWith(displayColumnPrefix) && column.length > displayColumnPrefix.length) {
            column.removePrefix(displayColumnPrefix)
        } else {
            null
        }
}

/**
 * Orders two display values [a] and [b] for a sort column of [kind] (issue #666). A blank value -- a row without
 * the trait, or one whose value does not read as the kind -- sorts **last** whichever way [descending] points,
 * since "no value" is not part of the ordered range; otherwise a number and a date compare by their parsed value
 * and text case-insensitively, reversed for [descending]. The caller composes this with a total tiebreak (the
 * id), so equal values still page in a stable order. The same string-then-parse-per-kind reading search's bounds
 * use ([matchesOne]); pure, and tested in the kernel.
 *
 * A text column with choices orders by them instead (issue #1500), given as their [choiceRanks]: in their declared
 * order, which is often meaningful (beginners, then some experience, then experts) where the stored values'
 * alphabetical order is not, and the labels the column shows would not read as sorted by value. A value that is not
 * one of them follows them, by text.
 */
fun compareForSort(
    a: String,
    b: String,
    kind: UsageKind,
    descending: Boolean,
    ranks: Map<String, Int> = emptyMap(),
): Int = when (kind) {
    UsageKind.string -> if (ranks.isEmpty()) {
        nullsLastForSort(a.trim().lowercase().ifEmpty { null }, b.trim().lowercase().ifEmpty { null }, descending)
    } else {
        compareByChoices(a, b, ranks, descending)
    }
    UsageKind.number -> nullsLastForSort(a.trim().toDoubleOrNull(), b.trim().toDoubleOrNull(), descending)
    UsageKind.date -> nullsLastForSort(
        a.parseDateOrNull(),
        b.parseDateOrNull(),
        descending,
    )
    // No before Yes (issue #1508); a blank, or a value that is not a boolean, last.
    UsageKind.boolean -> nullsLastForSort(parseExactBool(a), parseExactBool(b), descending)
}

/**
 * Each of [choices]' values by its place among them (issue #1500): what [compareForSort] orders a column with choices by,
 * built once per sort rather than searched on every comparison.
 */
fun choiceRanks(choices: List<SchOption>): Map<String, Int> = buildMap {
    // A repeated value keeps its first place, as the list reads.
    choices.forEachIndexed { i, choice -> if (choice.value !in this) put(choice.value, i) }
}

/** [compareForSort] for a column with [ranks]: by place among its choices, then by text, a blank last either way. */
private fun compareByChoices(a: String, b: String, ranks: Map<String, Int>, descending: Boolean): Int {
    fun keyOf(value: String): Pair<Int, String>? = value.trim().ifEmpty { null }?.let { v ->
        (ranks[v] ?: Int.MAX_VALUE) to v.lowercase()
    }
    val ka = keyOf(a)
    val kb = keyOf(b)
    if (ka == null || kb == null) return nullsLastForSort(ka?.second, kb?.second, descending)
    val c = compareValuesBy(ka, kb, { it.first }, { it.second })
    return if (descending) -c else c
}

/** Orders [a] against [b] with a null (a blank or unreadable value) always **last**, and the non-null pair
 *  reversed for [descending] (issue #666). */
private fun <T : Comparable<T>> nullsLastForSort(a: T?, b: T?, descending: Boolean): Int = when {
    a == null && b == null -> 0
    a == null -> 1
    b == null -> -1
    else -> if (descending) b.compareTo(a) else a.compareTo(b)
}
